import { IHttpService, IPromise, IRequestShortcutConfig, IRootScopeService } from "angular";
import { IRestLayerRequest } from "@gtmhub/core/rest-layer";
import { EnvironmentService } from "@gtmhub/env";
import { IInsight } from "@gtmhub/insights";
import { IReport } from "@gtmhub/reporting/models";
import { ITag } from "@gtmhub/tags/models";
import { ICollection } from "@webapp/core/core.models";
import {
  ICachedInsightResponse,
  IDirectSubmitInsightboard,
  IInsightBoardItemResponse,
  IInsightboard,
  IInsightboardItem,
  IInsightboardItemSetting,
  IInsightboardTransactions,
  IInstalledInsightboardTemplatesResponse,
  IMarketplaceInsightboardTemplate,
} from "../models/models";
import { DashboardEvents } from "../services/events";

export class InsightboardService {
  public static $inject = ["$http", "$rootScope", "EnvironmentService"];
  private events: DashboardEvents;

  constructor(private $http: IHttpService, $rootScope: IRootScopeService, private env: EnvironmentService) {
    this.events = new DashboardEvents($rootScope);
  }

  public removeTagFromInsightboard(tag: ITag, insightboardId: string): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/dashboards/" + insightboardId + "/tags");
    return this.$http({
      method: "DELETE",
      url: url,
      data: { title: tag.title },
      headers: {
        "Content-type": "application/json;charset=utf-8",
      },
    });
  }

  public addTag(insightboard: IInsightboard, tag: ITag): IPromise<unknown> {
    const url = this.env.getApiEndpoint("/dashboards/" + insightboard.id + "/tags");

    return this.$http.post(url, tag).then((response) => response.data);
  }

  public addInsightToInsightboard = (dashboardId: string, insight: IInsight): IPromise<void> => {
    const initialBoardFields =
      "id,insights{col,filters,id,insightId,insightName,insightTitle,row,sizeX,sizeY,insightState,insightSource,insightRequiredEntities,formatting,type,sectionHeaderId},name," +
      "sectionHeaders{col,id,row,sizeX,sizeY,sectionHeader,type,sectionHeaderId}," +
      "parameters{defaultValue,entityName,entityTitleField,entityValueField,id,key,predefinedValues,selectorType,title}," +
      "readOnly,settings{name,value,valueDisplayName,valueDisplayNameAsArray,dynamicValues},tags{name,title},isReport";

    return this.getInsightboard(dashboardId, initialBoardFields).then((insightboard) => {
      const newWidget: IInsightboardItem = {
        id: dashboardId,
        dashboardId: dashboardId,
        sizeX: insight.size.width,
        sizeY: insight.size.height,
        insightName: insight.name,
        insightId: insight.id,
        type: "insight",
        filters: angular.copy(insight.usedFilters),
      };

      insightboard.insights.unshift(newWidget);
      return this.saveInsightboardItems(dashboardId, [...insightboard.insights, ...insightboard.sectionHeaders]).then(() => null);
    });
  };

  public getInsightboard(id: string, fields: string = null): IPromise<IInsightboard> {
    const url = this.env.getApiEndpoint("/dashboards/" + id);
    const query: IRequestShortcutConfig = {
      params: {
        fields: fields,
      },
    };

    return this.$http.get<IInsightboard>(url, query).then((response) => response.data);
  }

  public getInsightboardsV2(params: IRestLayerRequest): IPromise<ICollection<IInsightboard>> {
    const url = this.env.getApiEndpointV2("/dashboards");
    return this.$http.get<ICollection<IInsightboard>>(url, { params }).then((response) => response.data);
  }

  public getFeaturedInsightboards(): IPromise<ICollection<IMarketplaceInsightboardTemplate>> {
    const url = this.env.getMarketplaceEndpointV2("/templates/insight-boards?featured=true");
    return this.$http.get<ICollection<IMarketplaceInsightboardTemplate>>(url).then((response) => response.data);
  }

  public getReports(): IPromise<IReport[]> {
    const url = this.env.getApiEndpoint("/dashboards/reports");
    return this.$http.get<IReport[]>(url).then((response) => response.data);
  }

  public createInsightboard(insightboard: IInsightboard): IPromise<IInsightboard> {
    const url = this.env.getApiEndpoint("/dashboards");

    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          teamsWithSubteamsPermissionsCount: this.getTeamsWithSubteamsPermissionsCount(insightboard),
        },
      },
    };

    return this.$http.post<IInsightboard>(url, insightboard, gtmhubAdditionalParams).then((response) => response.data);
  }

  public installMarketplaceInsightboards(insightboardIds: string[]): IPromise<IInstalledInsightboardTemplatesResponse> {
    const url = this.env.getMarketplaceEndpointV2("/templates/insight-boards/install");
    const body = {
      ids: insightboardIds,
    };

    return this.$http.post<IInstalledInsightboardTemplatesResponse>(url, body).then((response) => response.data);
  }

  public updateInsightboard(insightboard: IInsightboard): IPromise<IInsightboard> {
    const url: string = this.env.getApiEndpoint("/dashboards/" + insightboard.id);
    const body = {
      name: insightboard.name,
      access: insightboard.access,
      settings: insightboard.settings,
      archived: insightboard.archived,
    };
    const gtmhubAdditionalParams: IRequestShortcutConfig = {
      params: {
        gtmhubAdditionalParams: {
          teamsWithSubteamsPermissionsCount: this.getTeamsWithSubteamsPermissionsCount(insightboard),
        },
      },
    };

    return this.$http.put<IInsightboard>(url, body, gtmhubAdditionalParams).then((response) => {
      this.events.broadcastDashboardUpdated(insightboard);
      return response.data;
    });
  }

  public editInsightboardSettings(id: string, name: string, settings?: IInsightboardItemSetting[]): IPromise<IInsightboard> {
    const url = this.env.getApiEndpoint("/dashboards/" + id);
    const body = {
      name: name,
      settings: settings,
    };

    return this.$http.put<IInsightboard>(url, body).then((response) => response.data);
  }

  public deleteInsightboard(id: string): IPromise<void> {
    const url = this.env.getApiEndpoint("/dashboards/" + id);
    return this.$http.delete(url).then(() => {
      this.events.broadcastDashboardDeleted(id);
    });
  }

  public saveInsightboardItems(insightboardId: string, items: IInsightboardItem[]): IPromise<IInsightBoardItemResponse> {
    const url = this.env.getApiEndpoint("/dashboards/" + insightboardId + "/items");

    return this.$http.post<IInsightBoardItemResponse>(url, items).then((response) => response.data);
  }

  public saveInsightboardItem(item: IInsightboardItem): IPromise<boolean> {
    const url = this.env.getApiEndpoint("/dashboards/items/" + item.id);

    return this.$http.put<boolean>(url, item).then((response) => response.data);
  }

  public cloneInsightboard(insightboardId: string, newInsightboardTag: string): IPromise<IInsightboard> {
    const url: string = this.env.getApiEndpoint(`/dashboards/${insightboardId}/clone`);
    const payload: { tag: string } = {
      tag: newInsightboardTag,
    };

    return this.$http
      .post<IInsightboard>(url, payload, {
        params: { gtmhubAdditionalParams: { cloned: true } },
      })
      .then((response) => response.data);
  }

  public calculateInsightboardWidgets(
    insightboardId: string,
    parameters: IInsightboardItemSetting[] = null,
    excludeInsightIds: string[] = null
  ): IPromise<ICollection<IInsightboardTransactions>> {
    const url: string = this.env.getApiEndpoint(`/dashboards/${insightboardId}/calculate`);
    const payload: { parameters: IInsightboardItemSetting[]; excludeInsightIds: string[] } = {
      parameters: parameters,
      excludeInsightIds: excludeInsightIds,
    };

    return this.$http.post<ICollection<IInsightboardTransactions>>(url, payload).then((response) => response.data);
  }

  public getCachedInsightboardWidgets(insightboardId: string, parameters: IInsightboardItemSetting[] = null): IPromise<Array<ICachedInsightResponse>> {
    const url: string = this.env.getApiEndpoint(`/dashboards/${insightboardId}/cached/calculate`);
    const payload: { parameters: IInsightboardItemSetting[] } = {
      parameters: parameters,
    };

    return this.$http.post<Array<ICachedInsightResponse>>(url, payload).then((response) => response.data);
  }

  public directSubmitInsightboardToMarketplace(boardData: IDirectSubmitInsightboard): IPromise<IMarketplaceInsightboardTemplate> {
    const url: string = this.env.getMarketplaceEndpointV2("/templates/insight-boards/direct");

    return this.$http.post<IMarketplaceInsightboardTemplate>(url, boardData).then((response) => response.data);
  }

  private getTeamsWithSubteamsPermissionsCount(insightboard: IInsightboard): number {
    if (!insightboard.access || !insightboard.access.permissions) {
      return 0;
    }
    return insightboard.access.permissions.filter((permission) => permission.principalKind === "team_and_subteams").length;
  }
}
